import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useLocation } from 'react-router-dom';
import { Dropdown, Popconfirm, Spin } from 'antd';

import { DEFAULT_DATE_FORMAT } from '@/contants/common';
import { formatDate } from '@/utils/date';
import { useGetOCTableViewColumns } from '@/views/screens/Claims/OverCharge/hooks';

import { ReactComponent as MoreIcon } from '../../../../../assets/icons/dots-v.svg';
import { routeFileSelector } from '../../../../../utilz/i18n';
import Button from '../../../../components/Buttons/Button';
import FormCheckBox from '../../../../components/Inputs/FormCheckBox';
import BatchLabel from '../../../../components/Labels/BatchLabel';
import PrimaryLabel from '../../../../components/Labels/PrimaryLabel';
import SecondaryLabel from '../../../../components/Labels/SecondaryLabel';
import ScrollPagination from '../../../../components/Paginations/ScrollPagination';
import Paper from '../../../../components/Paper/Paper';
import ContentSpinner from '../../../../components/Spinners/ContentSpinner';

const CHECKBOX_COLUMN_WIDTH = 32;

const OCClaimTableView = ({
  handleLoadMore,
  isLoading,
  hasMore,
  claimsList,
  handleCardClick,
  onSelect,
  selectedClaims,
  handleDelete = () => [],
  isFirstTime,
}) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const { data: tableViewColumns = [], isFetching: isTableColumnsLoading } = useGetOCTableViewColumns();

  const handleCheckBoxClick = (e, data, isSelected) => {
    e.stopPropagation();
    onSelect(data.fbocKey, isSelected);
  };

  const colSpan = useMemo(
    // biome-ignore lint/style/noMagicNumbers: TODO: investigate>
    () => (tableViewColumns.length ? tableViewColumns.length : 0) + 3,
    [tableViewColumns.length],
  );

  return isFirstTime ? (
    <Paper className="h-100 w-100 mb-15">
      <ContentSpinner />
    </Paper>
  ) : (
    <div className="h-100 overflow-hidden">
      {isTableColumnsLoading ? (
        <div className="h-100 background-white br-4">
          <ContentSpinner />
        </div>
      ) : (
        <ScrollPagination
          className="background-white br-4 w-100 h-100 scrollable-table-container horizontal"
          loadMore={handleLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          scrollOffset={50}
          hideSpinner
        >
          <table className="w-100 background-white br-4">
            <thead>
              <tr>
                <th
                  style={{
                    maxWidth: CHECKBOX_COLUMN_WIDTH,
                    minWidth: CHECKBOX_COLUMN_WIDTH,
                    width: CHECKBOX_COLUMN_WIDTH,
                  }}
                  className="background-header text-white fixed fixed-top fixed-left shadow-none"
                />
                <th
                  className="background-header text-white fixed fixed-top fixed-left"
                  style={{
                    left: CHECKBOX_COLUMN_WIDTH,
                    whiteSpace: 'pre-wrap !important',
                  }}
                >
                  Claim Number
                </th>
                {tableViewColumns.map(({ columnName, columnValue }) => (
                  <th key={columnValue} className="background-header text-white fixed fixed-top">
                    {columnName}
                  </th>
                ))}
                <th className="background-header text-white fixed fixed-top fixed-right" />
              </tr>
            </thead>
            <tbody>
              {claimsList.map((row) => (
                <tr key={row.fbocKey}>
                  <td
                    className="fixed fixed-left shadow-none"
                    style={{
                      maxWidth: CHECKBOX_COLUMN_WIDTH,
                      minWidth: CHECKBOX_COLUMN_WIDTH,
                      width: CHECKBOX_COLUMN_WIDTH,
                    }}
                  >
                    <FormCheckBox
                      className="m-0"
                      checkBoxClass="normal-checkbox"
                      checked={selectedClaims.includes(row.fbocKey)}
                      onChange={(e) => handleCheckBoxClick(e, row, selectedClaims.includes(row.fbocKey))}
                    />
                  </td>
                  <td className="fixed fixed-left" style={{ left: CHECKBOX_COLUMN_WIDTH }}>
                    <span
                      className="cursor-pointer f-18 fw-700 text-header"
                      onClick={() => handleCardClick(row.fbocKey)}
                      role="button"
                      tabIndex={0}
                    >
                      {row.customerClaimNo}
                    </span>
                  </td>
                  {tableViewColumns.map(({ columnValue }) => (
                    <GenerateColumnValues key={columnValue} fieldName={columnValue} value={row[columnValue]} />
                  ))}
                  <td className="pl-5 pr-8 fixed fixed-right text-center">
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: '2',
                            label: (
                              <Popconfirm
                                placement="topRight"
                                title={t('translation:default.fields.deleteConfirm.title')}
                                onConfirm={() => handleDelete(row.fbocKey)}
                                okText={t('translation:default.options.yes')}
                                cancelText={t('translation:default.buttons.cancel')}
                              >
                                <span>{t('translation:default.buttons.delete')}</span>
                              </Popconfirm>
                            ),
                          },
                        ],
                      }}
                      placement="bottomLeft"
                    >
                      <Button
                        ghost
                        className="bg-none border-none w-0 p-0 shadow-none"
                        size="small"
                        shape="circle"
                        type="ghost"
                      >
                        <MoreIcon />
                      </Button>
                    </Dropdown>
                  </td>
                </tr>
              ))}
              {isLoading ? (
                <tr>
                  <td colSpan={colSpan} className="background-white text-center">
                    <Spin spinning />
                  </td>
                </tr>
              ) : null}
              {!isLoading && claimsList?.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="background-white text-center ">
                    <PrimaryLabel className="text-danger">
                      {t('translation:default.notification.emptyList')}.{'  '}
                      {t('translation:default.emptyRecords.title')}.
                    </PrimaryLabel>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </ScrollPagination>
      )}
    </div>
  );
};

const GenerateColumnValues = memo(({ fieldName, value }) => {
  const generateFieldValues = (fieldName, value) => {
    switch (fieldName) {
      case 'claimStatusDescription':
        return (
          <td key={fieldName}>
            <BatchLabel>{value}</BatchLabel>
          </td>
        );

      case 'overchargeAmount':
        return (
          <td key={fieldName}>
            <PrimaryLabel className="text-iconDanger">
              <NumericFormat
                thousandsGroupStyle="thousand"
                displayType="text"
                value={Number(value).toFixed(2)}
                thousandSeparator
              />
            </PrimaryLabel>
          </td>
        );

      case 'recoveryAmount':
        return (
          <td key={fieldName}>
            <PrimaryLabel className="text-success">
              <NumericFormat
                thousandsGroupStyle="thousand"
                displayType="text"
                value={Number(value).toFixed(2)}
                thousandSeparator
              />
            </PrimaryLabel>
          </td>
        );
      case 'invoiceNumber':
      case 'packageNumber':
        return (
          <td key={fieldName}>
            <SecondaryLabel className="f-15 fw-800 text-black">{value}</SecondaryLabel>
          </td>
        );

      case 'dateSubmitted':
      case 'createdOn':
      case 'modifiedOn':
      case 'closedDate':
      case 'ammendmentDate':
        return (
          <td key={fieldName}>
            <SecondaryLabel className="f-15 fw-800 text-black">
              {value ? formatDate(value, DEFAULT_DATE_FORMAT) : 'NA'}
            </SecondaryLabel>
          </td>
        );

      default:
        return (
          <td key={fieldName}>
            <SecondaryLabel className="f-15 fw-600 text-black">{value}</SecondaryLabel>
          </td>
        );
    }
  };
  return <>{generateFieldValues(fieldName, value)}</>;
});

export default OCClaimTableView;
